/**
 * VoxelField — collision-oriented view of the packed voxel world.
 *
 * The collision surface is the zero level set of the TRILINEAR interpolation of decoded
 * corner weights. Samples live at grid corners: the value at global voxel (gx,gy,gz) is
 * the field at world (gx*VOXEL_SCALE, gy*VOXEL_SCALE, gz*VOXEL_SCALE) — the same convention
 * as the terrain generator, the build rasterizer, and the SurfaceNets mesher, which is why
 * this field matches the rendered surface to well under half a voxel.
 *
 * Three exploitable properties of trilinear interpolation (the whole solver rests on these):
 *  1. Convexity: a cell's field values lie within [min, max] of its 8 corners — all-corners-air
 *     classifies the whole cell as air in O(8).
 *  2. Per-axis linearity: along any axis-aligned line the field is piecewise LINEAR with
 *     breakpoints at grid planes — vertical ground/ceiling crossings are computed exactly,
 *     with no iteration and no epsilon tuning. A floor of any thickness cannot be skipped
 *     at any speed because the entire swept interval is solved analytically.
 *  3. C0 continuity across chunk seams for free: every consumer reads a given global corner
 *     from its owning chunk, so both sides of a seam see identical values (no analogue of
 *     the mesher's boundary-ownership problem).
 *
 * The weight is a truncated NON-METRIC signed field (it saturates at ±0.5 and was written
 * at two different scales by terrain gen vs build edits). It is used ONLY for sign tests and
 * crossing interpolation — never as a metric distance.
 *
 * Sign convention (matches isVoxelSolid + the mesher): solid ⟺ decoded weight > 0 ⟺ packed ≥ 8.
 * No packed value decodes to exactly 0, so corner signs are never ambiguous. Point samples
 * treat Φ ≥ 0 as solid (conservative).
 *
 * Collidability: weight-solid corners whose material type is not SOLID (water, lava, leaves)
 * are substituted with COLLISION_FILTER_WEIGHT — byte-matching the mesher's FILTER_WEIGHT
 * rule, so the movement collision isosurface equals the rendered solid layer's isosurface.
 */
import { CHUNK_SIZE, VOXEL_SCALE, LIGHT_BITS, MATERIAL_BITS, VOXEL_STATIC_MASK, WEIGHT_MAX_PACKED, WEIGHT_MIN, WEIGHT_RANGE, CHUNK_SHIFT, CHUNK_LOCAL_MASK, INV_CHUNK_WORLD_SIZE, } from '../voxel/constants.js';
import { MATERIAL_TYPE_LUT, MAT_TYPE_SOLID, MAT_TYPE_LIQUID, MAT_TYPE_TRANSPARENT, } from '../materials/Materials.js';
import { COLLISION_FILTER_WEIGHT } from '../protocol/physics.js';
import { packChunkKey } from '../util/packedKey.js';
import { RAY_SOLID, RAY_LIQUID, RAY_TRANSPARENT } from './types.js';
export const INV_VOXEL_SCALE = 1 / VOXEL_SCALE;
/** Chunk knowledge states (see VoxelPhysicsWorld). */
export const CHUNK_UNKNOWN = 0;
export const CHUNK_LOADED = 1;
export const CHUNK_KNOWN_AIR = 2;
// ============== Static-word LUTs (2048 entries, visibility.ts pattern) ==============
// Index by (packed & VOXEL_STATIC_MASK) >> LIGHT_BITS → [weight:4 | material:7].
const LUT_SIZE = 1 << (16 - LIGHT_BITS); // 2048
const MAT_MASK_IN_IDX = (1 << MATERIAL_BITS) - 1;
/** Raw decoded weight, no material filtering. */
const LUT_W_RAW = new Float64Array(LUT_SIZE);
/** Movement/solid collision field: non-SOLID-type positive corners filtered out. */
const LUT_W_SOLID = new Float64Array(LUT_SIZE);
/** Liquid field (swim queries): non-LIQUID-type positive corners filtered out. */
const LUT_W_LIQUID = new Float64Array(LUT_SIZE);
/** Material type per static word (MAT_TYPE_*). */
const LUT_MTYPE = new Uint8Array(LUT_SIZE);
/** Material id per static word. */
const LUT_MAT = new Uint8Array(LUT_SIZE);
for (let idx = 0; idx < LUT_SIZE; idx++) {
    const mat = idx & MAT_MASK_IN_IDX;
    const pw = idx >> MATERIAL_BITS;
    const w = (pw / WEIGHT_MAX_PACKED) * WEIGHT_RANGE + WEIGHT_MIN;
    const mtype = MATERIAL_TYPE_LUT[mat];
    LUT_W_RAW[idx] = w;
    LUT_MTYPE[idx] = mtype;
    LUT_MAT[idx] = mat;
    LUT_W_SOLID[idx] = w > 0 && mtype !== MAT_TYPE_SOLID ? COLLISION_FILTER_WEIGHT : w;
    LUT_W_LIQUID[idx] = w > 0 && mtype !== MAT_TYPE_LIQUID ? COLLISION_FILTER_WEIGHT : w;
}
/** Map a MAT_TYPE_* value to its RAY_* mask bit. */
const TYPE_TO_MASK_BIT = new Uint8Array(3);
TYPE_TO_MASK_BIT[MAT_TYPE_SOLID] = RAY_SOLID;
TYPE_TO_MASK_BIT[MAT_TYPE_TRANSPARENT] = RAY_TRANSPARENT;
TYPE_TO_MASK_BIT[MAT_TYPE_LIQUID] = RAY_LIQUID;
// Weight assumed for corners inside UNKNOWN chunks on the post-region-check hot path.
// Conservative: unknown reads as fully solid, so a bug in region margins fails "safe"
// (blocks movement) rather than letting the capsule through.
const UNKNOWN_W = 0.5;
export class VoxelField {
    source;
    /** Packed-key chunk cache: Uint32Array = loaded, 0 = known-air. UNKNOWN is never cached. */
    cache = new Map();
    /**
     * Last-chunk pointer, keyed by the raw coordinate triple rather than a packed key.
     * Corner fetches are the hottest path in the engine (hundreds to ~1500 per tick) and
     * overwhelmingly hit one chunk, so the fast path must be three integer compares — packing
     * a key costs two multiplies and a Map hash on every single fetch.
     */
    lastCx = NaN;
    lastCy = NaN;
    lastCz = NaN;
    lastData = null;
    // Scratch (instance-level; the engine is single-threaded and non-reentrant by design)
    cw = new Float64Array(8);
    constructor(source) {
        this.source = source;
    }
    /** Drop a cached chunk (call on chunk ingest and unload; in-place edits need nothing). */
    invalidateChunk(cx, cy, cz) {
        this.cache.delete(packChunkKey(cx, cy, cz));
        if (cx === this.lastCx && cy === this.lastCy && cz === this.lastCz)
            this.clearPointer();
    }
    /** Drop the whole cache (world refresh / reload). */
    invalidateAll() {
        this.cache.clear();
        this.clearPointer();
    }
    clearPointer() {
        this.lastCx = NaN;
        this.lastCy = NaN;
        this.lastCz = NaN;
        this.lastData = null;
    }
    /** Resolve a chunk: Uint32Array = loaded, 0 = known-air, null = unknown (never cached). */
    resolveChunk(cx, cy, cz) {
        if (cx === this.lastCx && cy === this.lastCy && cz === this.lastCz)
            return this.lastData;
        const key = packChunkKey(cx, cy, cz);
        let val = this.cache.get(key);
        if (val === undefined) {
            const data = this.source.getChunkData(cx, cy, cz);
            if (data) {
                val = data;
            }
            else if (this.source.isKnownAir(cx, cy, cz)) {
                val = 0;
            }
            else {
                // UNKNOWN — never memoize, not even in the last-chunk pointer. The chunk may stream
                // in at any moment, and isRegionKnown short-circuits on the FIRST unknown chunk, so
                // caching it here would re-answer "unknown" from the pointer every tick and freeze
                // the player permanently even after the data arrived.
                return null;
            }
            this.cache.set(key, val);
        }
        this.lastCx = cx;
        this.lastCy = cy;
        this.lastCz = cz;
        this.lastData = val;
        return val;
    }
    /** Chunk knowledge state at chunk coords. */
    chunkState(cx, cy, cz) {
        const v = this.resolveChunk(cx, cy, cz);
        if (v === null)
            return CHUNK_UNKNOWN;
        return v === 0 ? CHUNK_KNOWN_AIR : CHUNK_LOADED;
    }
    /** True when every chunk overlapping the world-space AABB is LOADED or KNOWN_AIR. */
    isRegionKnown(minX, minY, minZ, maxX, maxY, maxZ) {
        const c0x = Math.floor(minX * INV_CHUNK_WORLD_SIZE), c1x = Math.floor(maxX * INV_CHUNK_WORLD_SIZE);
        const c0y = Math.floor(minY * INV_CHUNK_WORLD_SIZE), c1y = Math.floor(maxY * INV_CHUNK_WORLD_SIZE);
        const c0z = Math.floor(minZ * INV_CHUNK_WORLD_SIZE), c1z = Math.floor(maxZ * INV_CHUNK_WORLD_SIZE);
        for (let cx = c0x; cx <= c1x; cx++) {
            for (let cy = c0y; cy <= c1y; cy++) {
                for (let cz = c0z; cz <= c1z; cz++) {
                    if (this.resolveChunk(cx, cy, cz) === null)
                        return false;
                }
            }
        }
        return true;
    }
    /** Packed voxel word at global voxel coords, or -1 when the chunk is UNKNOWN. */
    voxelAt(gx, gy, gz) {
        const chunk = this.resolveChunk(gx >> CHUNK_SHIFT, gy >> CHUNK_SHIFT, gz >> CHUNK_SHIFT);
        if (chunk === null)
            return -1;
        if (chunk === 0)
            return 0; // known-air chunk: packed 0 IS air
        return chunk[(gx & CHUNK_LOCAL_MASK) + (gy & CHUNK_LOCAL_MASK) * CHUNK_SIZE + (gz & CHUNK_LOCAL_MASK) * CHUNK_SIZE * CHUNK_SIZE];
    }
    /** Solid-collision corner weight at global voxel coords; UNKNOWN reads as fully solid. */
    cornerSolidW(gx, gy, gz) {
        const v = this.voxelAt(gx, gy, gz);
        if (v < 0)
            return UNKNOWN_W;
        return LUT_W_SOLID[(v & VOXEL_STATIC_MASK) >> LIGHT_BITS];
    }
    /** Liquid-field corner weight; UNKNOWN reads as NOT liquid (swim never triggers on missing data). */
    cornerLiquidW(gx, gy, gz) {
        const v = this.voxelAt(gx, gy, gz);
        if (v < 0)
            return COLLISION_FILTER_WEIGHT;
        return LUT_W_LIQUID[(v & VOXEL_STATIC_MASK) >> LIGHT_BITS];
    }
    /** Mask-adjusted corner weight for raycasts; UNKNOWN reads as fully solid. */
    cornerMaskedW(gx, gy, gz, mask) {
        const v = this.voxelAt(gx, gy, gz);
        if (v < 0)
            return UNKNOWN_W;
        const idx = (v & VOXEL_STATIC_MASK) >> LIGHT_BITS;
        const w = LUT_W_RAW[idx];
        if (w > 0 && (TYPE_TO_MASK_BIT[LUT_MTYPE[idx]] & mask) === 0)
            return COLLISION_FILTER_WEIGHT;
        return w;
    }
    /** Trilinear solid-field sample at a world point. Φ ≥ 0 means inside solid. */
    sampleSolid(x, y, z) {
        const u = x * INV_VOXEL_SCALE;
        const v = y * INV_VOXEL_SCALE;
        const w = z * INV_VOXEL_SCALE;
        const gx = Math.floor(u), gy = Math.floor(v), gz = Math.floor(w);
        const fx = u - gx, fy = v - gy, fz = w - gz;
        const c = this.cw;
        c[0] = this.cornerSolidW(gx, gy, gz);
        c[1] = this.cornerSolidW(gx + 1, gy, gz);
        c[2] = this.cornerSolidW(gx, gy + 1, gz);
        c[3] = this.cornerSolidW(gx + 1, gy + 1, gz);
        c[4] = this.cornerSolidW(gx, gy, gz + 1);
        c[5] = this.cornerSolidW(gx + 1, gy, gz + 1);
        c[6] = this.cornerSolidW(gx, gy + 1, gz + 1);
        c[7] = this.cornerSolidW(gx + 1, gy + 1, gz + 1);
        const w00 = c[0] + (c[1] - c[0]) * fx;
        const w10 = c[2] + (c[3] - c[2]) * fx;
        const w01 = c[4] + (c[5] - c[4]) * fx;
        const w11 = c[6] + (c[7] - c[6]) * fx;
        const w0 = w00 + (w10 - w00) * fy;
        const w1 = w01 + (w11 - w01) * fy;
        return w0 + (w1 - w0) * fz;
    }
    /** Trilinear liquid-field sample at a world point. Φ ≥ 0 means inside liquid. */
    sampleLiquid(x, y, z) {
        const u = x * INV_VOXEL_SCALE;
        const v = y * INV_VOXEL_SCALE;
        const w = z * INV_VOXEL_SCALE;
        const gx = Math.floor(u), gy = Math.floor(v), gz = Math.floor(w);
        const fx = u - gx, fy = v - gy, fz = w - gz;
        const w000 = this.cornerLiquidW(gx, gy, gz);
        const w100 = this.cornerLiquidW(gx + 1, gy, gz);
        const w010 = this.cornerLiquidW(gx, gy + 1, gz);
        const w110 = this.cornerLiquidW(gx + 1, gy + 1, gz);
        const w001 = this.cornerLiquidW(gx, gy, gz + 1);
        const w101 = this.cornerLiquidW(gx + 1, gy, gz + 1);
        const w011 = this.cornerLiquidW(gx, gy + 1, gz + 1);
        const w111 = this.cornerLiquidW(gx + 1, gy + 1, gz + 1);
        const w00 = w000 + (w100 - w000) * fx;
        const w10 = w010 + (w110 - w010) * fx;
        const w01 = w001 + (w101 - w001) * fx;
        const w11 = w011 + (w111 - w011) * fx;
        const w0 = w00 + (w10 - w00) * fy;
        const w1 = w01 + (w11 - w01) * fy;
        return w0 + (w1 - w0) * fz;
    }
    /**
     * Analytic trilinear gradient of the solid field at a world point, written to `out`
     * (plain {x,y,z}). Grid-unit scale (constant factor — callers normalize).
     */
    gradSolid(x, y, z, out) {
        const u = x * INV_VOXEL_SCALE;
        const v = y * INV_VOXEL_SCALE;
        const w = z * INV_VOXEL_SCALE;
        const gx = Math.floor(u), gy = Math.floor(v), gz = Math.floor(w);
        const fx = u - gx, fy = v - gy, fz = w - gz;
        const c = this.cw;
        c[0] = this.cornerSolidW(gx, gy, gz);
        c[1] = this.cornerSolidW(gx + 1, gy, gz);
        c[2] = this.cornerSolidW(gx, gy + 1, gz);
        c[3] = this.cornerSolidW(gx + 1, gy + 1, gz);
        c[4] = this.cornerSolidW(gx, gy, gz + 1);
        c[5] = this.cornerSolidW(gx + 1, gy, gz + 1);
        c[6] = this.cornerSolidW(gx, gy + 1, gz + 1);
        c[7] = this.cornerSolidW(gx + 1, gy + 1, gz + 1);
        // x-lerps at the 4 (y,z) cell edges — shared by all three partials
        const x00 = c[0] + (c[1] - c[0]) * fx; // y0 z0
        const x10 = c[2] + (c[3] - c[2]) * fx; // y1 z0
        const x01 = c[4] + (c[5] - c[4]) * fx; // y0 z1
        const x11 = c[6] + (c[7] - c[6]) * fx; // y1 z1
        // d/dx: bilerp of the x-differences over (fy, fz)
        const a0 = (c[1] - c[0]) + ((c[3] - c[2]) - (c[1] - c[0])) * fy;
        const a1 = (c[5] - c[4]) + ((c[7] - c[6]) - (c[5] - c[4])) * fy;
        out.x = a0 + (a1 - a0) * fz;
        // d/dy: z-lerp of the y-differences of the x-lerps
        out.y = (x10 - x00) + ((x11 - x01) - (x10 - x00)) * fz;
        // d/dz: y-lerp of the z-differences of the x-lerps
        out.z = (x01 - x00) + ((x11 - x10) - (x01 - x00)) * fy;
    }
    /**
     * Highest solid crossing along a vertical line, searching DOWNWARD through [yBot, yTop].
     * Exact: the field restricted to a vertical line is piecewise linear between grid planes.
     * `out.startsSolid` = the field at yTop is already ≥ 0 (surface above the window).
     */
    columnCrossingDown(x, z, yTop, yBot, out) {
        out.found = false;
        out.startsSolid = false;
        if (yBot > yTop)
            return;
        const u = x * INV_VOXEL_SCALE;
        const wz = z * INV_VOXEL_SCALE;
        const bx = Math.floor(u), bz = Math.floor(wz);
        const fx = u - bx, fz = wz - bz;
        const c00 = (1 - fx) * (1 - fz), c10 = fx * (1 - fz), c01 = (1 - fx) * fz, c11 = fx * fz;
        const kTop = Math.floor(yTop * INV_VOXEL_SCALE);
        const kBot = Math.floor(yBot * INV_VOXEL_SCALE);
        // Weight at a horizontal grid plane k (bilinear over the 4 corner columns)
        let wHi = this.planeSolidW(bx, bz, kTop + 1, c00, c10, c01, c11);
        let yHi = (kTop + 1) * VOXEL_SCALE;
        for (let k = kTop; k >= kBot; k--) {
            const wLo = this.planeSolidW(bx, bz, k, c00, c10, c01, c11);
            const yLo = k * VOXEL_SCALE;
            // Clip the [yLo, yHi] linear segment to [yBot, yTop]
            const yA = yHi > yTop ? yTop : yHi;
            const yB = yLo < yBot ? yBot : yLo;
            if (yA >= yB) {
                const invSpan = 1 / (yHi - yLo); // span is VOXEL_SCALE, never 0
                const wA = wLo + (wHi - wLo) * ((yA - yLo) * invSpan);
                const wB = wLo + (wHi - wLo) * ((yB - yLo) * invSpan);
                if (wA >= 0) {
                    // The very top of the (clipped) window is already solid
                    out.found = true;
                    out.startsSolid = true;
                    out.y = yA;
                    return;
                }
                if (wB >= 0) {
                    // Air above, solid below — the crossing (exact linear root)
                    const t = wA / (wA - wB);
                    out.found = true;
                    out.y = yA + t * (yB - yA);
                    return;
                }
            }
            wHi = wLo;
            yHi = yLo;
        }
    }
    /**
     * Lowest solid crossing along a vertical line, searching UPWARD through [yBot, yTop]
     * (ceilings). `out.startsSolid` = the field at yBot is already ≥ 0.
     */
    columnCrossingUp(x, z, yBot, yTop, out) {
        out.found = false;
        out.startsSolid = false;
        if (yBot > yTop)
            return;
        const u = x * INV_VOXEL_SCALE;
        const wz = z * INV_VOXEL_SCALE;
        const bx = Math.floor(u), bz = Math.floor(wz);
        const fx = u - bx, fz = wz - bz;
        const c00 = (1 - fx) * (1 - fz), c10 = fx * (1 - fz), c01 = (1 - fx) * fz, c11 = fx * fz;
        const kBot = Math.floor(yBot * INV_VOXEL_SCALE);
        const kTop = Math.floor(yTop * INV_VOXEL_SCALE);
        let wLo = this.planeSolidW(bx, bz, kBot, c00, c10, c01, c11);
        let yLo = kBot * VOXEL_SCALE;
        for (let k = kBot + 1; k <= kTop + 1; k++) {
            const wHi = this.planeSolidW(bx, bz, k, c00, c10, c01, c11);
            const yHi = k * VOXEL_SCALE;
            const yA = yLo < yBot ? yBot : yLo; // lower clipped end
            const yB = yHi > yTop ? yTop : yHi; // upper clipped end
            if (yB >= yA) {
                const invSpan = 1 / (yHi - yLo);
                const wA = wLo + (wHi - wLo) * ((yA - yLo) * invSpan);
                const wB = wLo + (wHi - wLo) * ((yB - yLo) * invSpan);
                if (wA >= 0) {
                    out.found = true;
                    out.startsSolid = true;
                    out.y = yA;
                    return;
                }
                if (wB >= 0) {
                    const t = wA / (wA - wB);
                    out.found = true;
                    out.y = yA + t * (yB - yA);
                    return;
                }
            }
            wLo = wHi;
            yLo = yHi;
        }
    }
    planeSolidW(bx, bz, k, c00, c10, c01, c11) {
        return (this.cornerSolidW(bx, k, bz) * c00 +
            this.cornerSolidW(bx + 1, k, bz) * c10 +
            this.cornerSolidW(bx, k, bz + 1) * c01 +
            this.cornerSolidW(bx + 1, k, bz + 1) * c11);
    }
    /**
     * First solid crossing along an arbitrary segment (whiskers, horizontal sweeps).
     * Cells whose 8 corners are all air are skipped whole (convexity). Mixed cells are
     * sampled at 4 points across the clipped interval and the first straddling sub-interval
     * is bisected — the field along a line is at most cubic per cell and heavily band-limited
     * by 4-bit quantization, so this is robust in practice; the interior verifier backstops it.
     */
    segmentFirstCrossing(x0, y0, z0, x1, y1, z1, out) {
        out.found = false;
        out.startsSolid = false;
        out.t = 0;
        const w0 = this.sampleSolid(x0, y0, z0);
        if (w0 >= 0) {
            out.found = true;
            out.startsSolid = true;
            return;
        }
        const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
        const len2 = dx * dx + dy * dy + dz * dz;
        if (len2 === 0)
            return;
        // Walk cell boundaries parametrically (cells in grid units; corners at integers).
        const u0 = x0 * INV_VOXEL_SCALE, v0 = y0 * INV_VOXEL_SCALE, s0 = z0 * INV_VOXEL_SCALE;
        const du = dx * INV_VOXEL_SCALE, dv = dy * INV_VOXEL_SCALE, ds = dz * INV_VOXEL_SCALE;
        let tCur = 0;
        let guard = 0;
        while (tCur < 1 && guard++ < 256) {
            // Current cell
            const px = u0 + du * tCur, py = v0 + dv * tCur, pz = s0 + ds * tCur;
            const gx = Math.floor(px), gy = Math.floor(py), gz = Math.floor(pz);
            // Parametric exit of this cell
            let tExit = 1;
            if (du > 0)
                tExit = Math.min(tExit, (gx + 1 - u0) / du);
            else if (du < 0)
                tExit = Math.min(tExit, (gx - u0) / du);
            if (dv > 0)
                tExit = Math.min(tExit, (gy + 1 - v0) / dv);
            else if (dv < 0)
                tExit = Math.min(tExit, (gy - v0) / dv);
            if (ds > 0)
                tExit = Math.min(tExit, (gz + 1 - s0) / ds);
            else if (ds < 0)
                tExit = Math.min(tExit, (gz - s0) / ds);
            if (tExit <= tCur)
                tExit = tCur + 1e-7; // numeric safety on exact corners
            // Whole-cell air skip
            if (this.cornerSolidW(gx, gy, gz) < 0 &&
                this.cornerSolidW(gx + 1, gy, gz) < 0 &&
                this.cornerSolidW(gx, gy + 1, gz) < 0 &&
                this.cornerSolidW(gx + 1, gy + 1, gz) < 0 &&
                this.cornerSolidW(gx, gy, gz + 1) < 0 &&
                this.cornerSolidW(gx + 1, gy, gz + 1) < 0 &&
                this.cornerSolidW(gx, gy + 1, gz + 1) < 0 &&
                this.cornerSolidW(gx + 1, gy + 1, gz + 1) < 0) {
                tCur = tExit;
                continue;
            }
            // Mixed cell: sample the clipped interval at 4 points, bisect the first straddle
            const span = tExit - tCur;
            let tPrev = tCur;
            let wPrev = this.sampleSolid(x0 + dx * tPrev, y0 + dy * tPrev, z0 + dz * tPrev);
            for (let i = 1; i <= 3; i++) {
                const tNext = i === 3 ? tExit : tCur + (span * i) / 3;
                const wNext = this.sampleSolid(x0 + dx * tNext, y0 + dy * tNext, z0 + dz * tNext);
                if (wPrev < 0 && wNext >= 0) {
                    // Bisect [tPrev, tNext]
                    let a = tPrev, b = tNext;
                    for (let it = 0; it < 16; it++) {
                        const m = (a + b) * 0.5;
                        const wm = this.sampleSolid(x0 + dx * m, y0 + dy * m, z0 + dz * m);
                        if (wm >= 0)
                            b = m;
                        else
                            a = m;
                    }
                    out.found = true;
                    out.t = b;
                    return;
                }
                tPrev = tNext;
                wPrev = wNext;
            }
            tCur = tExit;
        }
    }
}
//# sourceMappingURL=voxelField.js.map